import { useCallback, useRef, useState } from 'react'
import classNames from 'classnames'
import { createPortal } from 'react-dom'

import useDebounce from 'hooks/useDebounce'

import './styles.scss'

type LegacyDirection = 'tooltippedN' | 'tooltippedS' | 'tooltippedE' | 'tooltippedW'
export type TooltipDirection = 'top' | 'bottom' | 'right' | 'left'

interface RCTooltipProps {
  children: React.ReactNode
  tooltipMessage: React.ReactNode
  /** @default 'bottom' */
  direction?: TooltipDirection
  /**
   * This is a legacy prop, it is used to support the old tooltip styles. It is recommended to use the `direction` prop instead.
   * @deprecated
   * @see {@link direction}
   */
  tooltipStyle?: LegacyDirection
  hideTooltip?: boolean
}

export function RCTooltip({
  children,
  tooltipMessage,
  direction = 'bottom',
  tooltipStyle,
  hideTooltip,
}: RCTooltipProps) {
  const tooltipId = `tooltip-${Math.random().toString(36).slice(2, 11)}`

  const [visible, setVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const tooltipClass = tooltipStyle || direction

  const calculatePosition = useCallback(
    (rect: DOMRect) => {
      switch (tooltipClass) {
        case 'top':
        case 'tooltippedN':
          return {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX + rect.width / 2,
          }
        case 'bottom':
        case 'tooltippedS':
          return {
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX + rect.width / 2,
          }
        case 'left':
        case 'tooltippedW':
          return {
            top: rect.top + window.scrollY + rect.height / 2,
            left: rect.left + window.scrollX,
          }
        case 'right':
        case 'tooltippedE':
          return {
            top: rect.top + window.scrollY + rect.height / 2,
            left: rect.right + window.scrollX,
          }
        default:
          return { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX }
      }
    },
    [tooltipClass]
  )

  const handleTooltipShow = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      setCoords(calculatePosition(rect))
      setVisible(true)
    }
  }, [calculatePosition])

  const { debouncedAction: handleMouseEnter, cancelDebouncedAction: cancelTooltip } = useDebounce(
    handleTooltipShow,
    200
  )

  const handleMouseLeave = () => {
    cancelTooltip()
    setVisible(false)
  }

  if (!tooltipMessage || hideTooltip) {
    return <>{children}</>
  }

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleTooltipShow}
        onBlur={handleMouseLeave}
        aria-describedby={tooltipId}
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className={classNames('RCTooltip', tooltipClass, { visible: visible })}
            style={{ ...coords }}
          >
            {tooltipMessage}
          </div>,
          document.querySelector('.ThemeProvider')
        )}
    </>
  )
}
